import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { InputHTMLAttributes } from 'react';
import { formatDmy } from './datePickerModel';
import { attachDateInputSync } from './dateInputSync';
import { attachDmyTyping, writeNativeValue } from './dateTyping';
import DateFieldOverlay from './DateFieldOverlay';

/**
 * P-55c (D-230 sec.7) - the date-only field of the system. It is still a real
 * `<input type="date">` (so the global DatePickerLayer opens on a click, forms and
 * FormData keep working), but the browser's own text (mm/dd/yyyy in a US browser)
 * is hidden and replaced by dd/mm/yyyy, whatever the browser locale.
 * Date + time fields (datetime-local) do not use it (QX-611).
 */
type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

const DateInput = forwardRef<HTMLInputElement, Props>(function DateInput({ className, ...rest }, ref) {
  const inner = useRef<HTMLInputElement | null>(null);
  const [shown, setShown] = useState(String(rest.value ?? rest.defaultValue ?? ''));
  const [draft, setDraft] = useState<string | null>(null); // FIX-B41 (K4): digits typed so far, as a dd/mm/yyyy mask

  const setRefs = useCallback((el: HTMLInputElement | null) => {
    inner.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
  }, [ref]);

  // Controlled: follow the prop. Uncontrolled: follow the field's own input/change events.
  useEffect(() => { if (rest.value !== undefined) setShown(String(rest.value)); }, [rest.value]);
  useEffect(() => {
    const el = inner.current;
    if (!el) return;
    return attachDateInputSync(el, setShown);
  }, []);
  // FIX-B41 (QX-931 / K4): hand typing is always dd/mm/yyyy, whatever the browser's segment order.
  useEffect(() => {
    const el = inner.current;
    if (!el) return;
    return attachDmyTyping(el, false, setDraft, (v) => writeNativeValue(el, v));
  }, []);

  const text = draft ?? formatDmy(shown);
  return (
    <span className="relative block">
      <input {...rest} ref={setRefs} type="date" className={`${className ?? 'input'} date-dmy`} />
      <DateFieldOverlay text={text} placeholder="dd/mm/yyyy" ltr={rest.dir === 'ltr'} disabled={!!rest.disabled} />
    </span>
  );
});

export default DateInput;
